import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calculator, BarChart3, ChevronDown, Gift } from 'lucide-react';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  formatOdds,
  formatMarketType,
  formatGameTime,
  sportsbookIcons,
  sportsbookNames,
  getOddsFormat,
  setOddsFormat as saveOddsFormat
} from '../utils/oddsUtils';
import DataBanner from './DataBanner';
import FilterBar from './FilterBar';
import MarketBadge from './MarketBadge';

function PromoConversion() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [betSize, setBetSize] = useState(100);
  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [selectedKeytypes, setSelectedKeytypes] = useState([]);
  const [dateLimit, setDateLimit] = useState(null);
  const [showOnlyMyBooks, setShowOnlyMyBooks] = useState(false);
  const [showWhatIs, setShowWhatIs] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [oddsFormat, setOddsFormat] = useState(getOddsFormat());
  const { getToken } = useAuth();
  const { user } = useUser();
  const myBooks = user?.unsafeMetadata?.selectedBooks || [];

  const handleOddsFormatChange = (format) => {
    setOddsFormat(format);
    saveOddsFormat(format);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/promo-conversion`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOpportunities(response.data);
      } catch (error) {
        console.error('Error fetching promo conversion:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getToken]);

  const availableLeagues = useMemo(() =>
    [...new Set(opportunities.map(o => o.sporttitle))].sort(),
    [opportunities]
  );
  const availableBooks = useMemo(() => {
    const books = new Set();
    opportunities.forEach(o => {
      if (o.bookmakerid1) books.add(o.bookmakerid1);
      if (o.bookmakerid2) books.add(o.bookmakerid2);
    });
    return [...books].sort((a, b) => (sportsbookNames[a] || a).localeCompare(sportsbookNames[b] || b));
  }, [opportunities]);
  const availableKeytypes = useMemo(() =>
    [...new Set(opportunities.map(o => o.keytype))].sort(),
    [opportunities]
  );

  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(opp => {
      if (dateLimit !== null) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() + dateLimit);
        cutoff.setHours(23, 59, 59, 999);
        if (new Date(opp.commencetime) > cutoff) return false;
      }
      if (selectedLeagues.length > 0 && !selectedLeagues.includes(opp.sporttitle)) return false;
      if (selectedBooks.length > 0 && !selectedBooks.includes(opp.bookmakerid1)) return false;
      if (showOnlyMyBooks && myBooks.length > 0 && (!myBooks.includes(opp.bookmakerid1) || !myBooks.includes(opp.bookmakerid2))) return false;
      if (selectedKeytypes.length > 0 && !selectedKeytypes.includes(opp.keytype)) return false;
      return true;
    });
  }, [opportunities, selectedLeagues, selectedBooks, selectedKeytypes, dateLimit, showOnlyMyBooks, myBooks]);

  if (loading) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a0e1a'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #2d3748',
            borderTop: '4px solid #00d9ff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#64748b', fontSize: '16px' }}>Loading bonus conversion opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '40px 60px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            margin: 0,
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            Bonus Bet Conversion
          </h1>

          {/* Odds Format Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'American' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              American
            </span>
            <div
              onClick={() => handleOddsFormatChange(oddsFormat === 'American' ? 'Decimal' : 'American')}
              style={{
                width: '48px',
                height: '26px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #2d3748',
                borderRadius: '13px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#00d9ff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: oddsFormat === 'American' ? '3px' : '23px',
                transition: 'left 0.2s ease'
              }} />
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'Decimal' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              Decimal
            </span>
          </div>
        </div>

        <DataBanner />

        {/* Description Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '10px'
        }}>
          <div
            onClick={() => setShowWhatIs(!showWhatIs)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              What is Bonus Bet Conversion?
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showWhatIs ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showWhatIs && (
            <div style={{ marginTop: '15px' }}>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 15px 0'
              }}>
                Sportsbooks offer bonus bets in many ways: sign-up promotions, reload bonuses, or as payouts
                when other bets win or lose. The catch? This money can't be withdrawn directly. It must be wagered.
                Bonus bet conversion is a strategy that turns these non-withdrawable credits into real cash.
              </p>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 15px 0'
              }}>
                The strategy is simple: place your bonus bet on one outcome, then hedge with
                real money on the opposite outcome at another sportsbook. No matter who wins, you walk away with
                real, withdrawable cash.
              </p>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 20px 0'
              }}>
                <span style={{ color: '#ffffff', fontWeight: '600' }}>Important:</span> Unlike normal bets, bonus bets
                don't return your stake. If you place a $100 bonus bet at +200 and win, you get $200 in profit, not $300.
                This affects the math, but you can still convert <span style={{ color: '#10b981', fontWeight: '600' }}>70-80%+</span> of
                your bonus bet into real money.
              </p>

              {/* Example Box */}
              <div style={{
                backgroundColor: '#0f1419',
                border: '1px solid #2d3748',
                borderRadius: '10px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '15px'
                }}>
                  Example: Converting a $100 Sign-Up Bonus
                </div>

                {/* Setup */}
                <div style={{
                  backgroundColor: '#1a1f2e',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  marginBottom: '15px'
                }}>
                  <p style={{ fontSize: '14px', color: '#a0aec0', margin: '0 0 0 0', lineHeight: '1.6' }}>
                    You receive a $100 bonus bet from
                    Sportsbook A. You find an event where Sportsbook A has good odds on one side, and Sportsbook B
                    has good odds on the opposite side.
                  </p>
                </div>

                {/* Sportsbook Odds Comparison */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  <div style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '10px' }}>Sportsbook A</div>
                    <div style={{ color: '#a0aec0', fontSize: '14px' }}>
                      Team 1 <span style={{
                        fontFamily: 'monospace',
                        fontWeight: '600',
                        color: '#00d9ff',
                        border: '1px solid #00d9ff',
                        borderRadius: '12px',
                        padding: '2px 8px'
                      }}>+400</span>
                      <span style={{ color: '#64748b', margin: '0 10px' }}>vs</span>
                      Team 2 <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>-500</span>
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '10px' }}>Sportsbook B</div>
                    <div style={{ color: '#a0aec0', fontSize: '14px' }}>
                      Team 1 <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>+380</span>
                      <span style={{ color: '#64748b', margin: '0 10px' }}>vs</span>
                      Team 2 <span style={{
                        fontFamily: 'monospace',
                        fontWeight: '600',
                        color: '#00d9ff',
                        border: '1px solid #00d9ff',
                        borderRadius: '12px',
                        padding: '2px 8px'
                      }}>-470</span>
                    </div>
                  </div>
                </div>

                {/* Best Odds & Bet Amounts */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '15px'
                }}>
                  <div style={{
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    padding: '15px 25px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '25px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Bonus Bet</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#00d9ff', fontFamily: 'monospace' }}>+400</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', fontFamily: 'monospace' }}>$100</div>
                    </div>
                    <div style={{ color: '#64748b', fontSize: '14px' }}>vs</div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Hedge Bet</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#00d9ff', fontFamily: 'monospace' }}>-470</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', fontFamily: 'monospace' }}>$330</div>
                    </div>
                  </div>
                </div>

                {/* Two Outcomes */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  {/* If Bonus Bet Wins */}
                  <div style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                      If Team 1 Wins
                    </div>
                    <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Bonus payout:</span>
                      <span style={{ color: '#10b981', fontFamily: 'monospace', fontWeight: '600' }}>+$400</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Hedge lost:</span>
                      <span style={{ color: '#f87171', fontFamily: 'monospace', fontWeight: '600' }}>-$330</span>
                    </div>
                    <div style={{ borderTop: '1px solid #2d3748', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: '#ffffff', fontWeight: '600' }}>Net profit:</span>
                      <span style={{ color: '#10b981', fontFamily: 'monospace', fontWeight: '700' }}>$70</span>
                    </div>
                  </div>

                  {/* If Hedge Wins */}
                  <div style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                      If Team 2 Wins
                    </div>
                    <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Hedge payout:</span>
                      <span style={{ color: '#10b981', fontFamily: 'monospace', fontWeight: '600' }}>+$400</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Bonus lost:</span>
                      <span style={{ color: '#a0aec0', fontFamily: 'monospace', fontWeight: '600' }}>$0</span>
                    </div>
                    <div style={{ borderTop: '1px solid #2d3748', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: '#ffffff', fontWeight: '600' }}>Net profit:</span>
                      <span style={{ color: '#10b981', fontFamily: 'monospace', fontWeight: '700' }}>$70</span>
                    </div>
                  </div>
                </div>

                {/* Result */}
                <div style={{
                  backgroundColor: '#0f1419',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  padding: '15px',
                  textAlign: 'center',
                  marginBottom: '15px'
                }}>
                  <div style={{ fontSize: '14px', color: '#a0aec0', marginBottom: '4px' }}>
                    Either way, <span style={{ color: '#10b981', fontWeight: '700', fontSize: '18px', fontFamily: 'monospace' }}>$70</span> profit
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>
                    70% Conversion
                  </div>
                </div>

                {/* Takeaway */}
                <div style={{
                  fontSize: '13px',
                  color: '#a0aec0',
                  lineHeight: '1.6'
                }}>
                  <span style={{ fontWeight: '600', color: '#ffffff' }}>Tip:</span> Longer odds and tighter spreads
                  create better conversion opportunities. Aim for 70-80%+
                </div>
              </div>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '20px'
        }}>
          <div
            onClick={() => setShowHowTo(!showHowTo)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              How to Use
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showHowTo ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showHowTo && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.8',
              color: '#a0aec0',
              marginTop: '15px'
            }}>
              <p style={{ margin: '0 0 10px 0' }}>
                1. Filter by the sportsbook where you have a bonus bet
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                2. Set your bonus bet amount to see the required hedge bet size
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                3. Review the conversion %, sportsbooks, and recommended bet sizes
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                4. Use the calculator (<Calculator size={16} color="#a0aec0" style={{ verticalAlign: 'middle' }} />) for detailed payout scenarios and verification
              </p>
              <p style={{ margin: 0 }}>
                5. Place your bonus bet on the displayed outcome, then hedge with the calculated amount
              </p>
            </div>
          )}
        </div>

        {/* Opportunities Table */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '20px'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '1px solid #2d3748'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
                border: '1px solid rgba(0, 217, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Gift size={16} color="#00d9ff" />
              </div>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#ffffff'
              }}>
                Live Opportunities
              </h2>
              <div style={{
                padding: '4px 12px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#10b981'
              }}>
                {filteredOpportunities.length} ACTIVE
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#a0aec0'
                }}>
                  Bet Size:
                </label>
                <input
                  type="number"
                  value={betSize}
                  onChange={(e) => setBetSize(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    width: '100px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    backgroundColor: '#0f1419',
                    border: '1px solid #2d3748',
                    borderRadius: '6px',
                    color: '#ffffff',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setBetSize(100);
                    }
                    e.target.style.borderColor = '#2d3748';
                  }}
                />
              </div>
            </div>
          </div>

          <FilterBar
            availableLeagues={availableLeagues}
            availableBooks={availableBooks}
            availableKeytypes={availableKeytypes}
            selectedLeagues={selectedLeagues}
            selectedBooks={selectedBooks}
            selectedKeytypes={selectedKeytypes}
            dateLimit={dateLimit}
            setSelectedLeagues={setSelectedLeagues}
            setSelectedBooks={setSelectedBooks}
            setSelectedKeytypes={setSelectedKeytypes}
            setDateLimit={setDateLimit}
            showOnlyMyBooks={showOnlyMyBooks}
            setShowOnlyMyBooks={setShowOnlyMyBooks}
          />

          {/* Column Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '80px 1.5fr 140px 1fr 1fr 50px',
            gap: '16px',
            alignItems: 'center',
            padding: '0 20px 12px 20px',
            borderBottom: '1px solid #2d3748',
            marginBottom: '12px'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Conversion
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Event
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Market
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Bonus Bet
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Hedge Bet
            </div>
            <div></div>
          </div>

          {/* Opportunities List */}
          {filteredOpportunities.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              color: '#64748b'
            }}>
              <BarChart3 size={48} color="#64748b" style={{ marginBottom: '20px', margin: '0 auto 20px' }} />
              <p style={{ fontSize: '16px', margin: 0 }}>
                No bonus conversion opportunities found. Try selecting a different sportsbook.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredOpportunities.map((opp, index) => {
                const conversionPercent = (opp.bonus_calc * 100).toFixed(1);

                return (
                  <div key={index} style={{
                    backgroundColor: '#0f1419',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#00d9ff';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 217, 255, 0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#2d3748';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1.5fr 140px 1fr 1fr 50px',
                      gap: '16px',
                      alignItems: 'center'
                    }}>
                      {/* Conversion % */}
                      <div style={{
                        fontSize: '22px',
                        fontWeight: '800',
                        fontFamily: 'monospace',
                        color: '#10b981',
                        textAlign: 'center'
                      }}>
                        {conversionPercent}%
                      </div>

                      {/* Event */}
                      <div>
                        <div style={{
                          fontWeight: '600',
                          fontSize: '14px',
                          color: '#ffffff',
                          marginBottom: '4px'
                        }}>
                          {opp.home_team} vs. {opp.away_team}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#64748b'
                        }}>
                          {opp.sporttitle} - {formatGameTime(opp.commencetime)}
                        </div>
                      </div>

                      {/* Market Type */}
                      <MarketBadge keytype={opp.keytype} playerName={opp.player_name} />

                      {/* Bonus Bet */}
                      <div style={{
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #2d3748',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        <div style={{
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#ffffff',
                          marginBottom: '6px'
                        }}>
                          {opp.name1} {opp.point1 ? `(${opp.point1})` : ''}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <img
                              src={`/sportsbook-icons/${sportsbookIcons[opp.bookmakerid1] || 'placeholder.png'}`}
                              alt={sportsbookNames[opp.bookmakerid1] || opp.bookmakerid1}
                              title={sportsbookNames[opp.bookmakerid1] || opp.bookmakerid1}
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '4px',
                                objectFit: 'contain'
                              }}
                            />
                            <span style={{
                              color: '#00d9ff',
                              fontFamily: 'monospace',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}>
                              {formatOdds(opp.price1, oddsFormat)}
                            </span>
                          </div>
                          <div style={{
                            fontSize: '15px',
                            color: '#00d9ff',
                            fontWeight: '700',
                            fontFamily: 'monospace'
                          }}>
                            ${Number(betSize)}
                          </div>
                        </div>
                      </div>

                      {/* Hedge Bet */}
                      <div style={{
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #2d3748',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        <div style={{
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#ffffff',
                          marginBottom: '6px'
                        }}>
                          {opp.name2} {opp.point2 ? `(${opp.point2})` : ''}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <img
                              src={`/sportsbook-icons/${sportsbookIcons[opp.bookmakerid2] || 'placeholder.png'}`}
                              alt={sportsbookNames[opp.bookmakerid2] || opp.bookmakerid2}
                              title={sportsbookNames[opp.bookmakerid2] || opp.bookmakerid2}
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '4px',
                                objectFit: 'contain'
                              }}
                            />
                            <span style={{
                              color: '#00d9ff',
                              fontFamily: 'monospace',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}>
                              {formatOdds(opp.price2, oddsFormat)}
                            </span>
                          </div>
                          <div style={{
                            fontSize: '15px',
                            color: '#00d9ff',
                            fontWeight: '700',
                            fontFamily: 'monospace'
                          }}>
                            ${Math.round(Number(betSize) * opp.bonus_calc_ratio)}
                          </div>
                        </div>
                      </div>

                      {/* Calculator Button */}
                      <Link
                        to="/promo-conversion-calculator"
                        state={{
                          bonusOdds: opp.price1,
                          hedgeOdds: opp.price2,
                          bonusBetSize: Number(betSize),
                          hedgeBetSize: Math.round(Number(betSize) * opp.bonus_calc_ratio)
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        <button style={{
                          width: '40px',
                          height: '40px',
                          padding: 0,
                          cursor: 'pointer',
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #2d3748',
                          borderRadius: '8px',
                          fontSize: '20px',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#00d9ff';
                          e.currentTarget.style.borderColor = '#00d9ff';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#1a1f2e';
                          e.currentTarget.style.borderColor = '#2d3748';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        >
                          <Calculator size={18} color="#a0aec0" />
                        </button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PromoConversion;
